import { CarTile } from '@kmx/car-tile';
import { CarTile as CarTileFuture, TaggingEvent } from '@kmx/car-tile-future';
import { useEffect, useMemo, useRef, useState } from 'react';
import { IFBSDecision } from '../../../../api/types/finance';
import { ITrackingPayload } from '../../../../types/analytics';
import { IVehicleTile } from '../../../../types/vehicleTile';
import * as analytics from '../../../../utilities/analytics';
import { ANALYTICS_CONSTANTS, trackEDDL } from '../../../../utilities/analytics';
import Icons from '../../../../utilities/icons';
import { getTileAvailabilityText } from '../../../../utilities/getTileAvailabilityText';
import useAppState from '../../../../common/state/appState';
import TileKebabMenuItemSnackbar from '../tile-kebab-menu/TileKebabMenuItemSnackbar';
import { mapVehicleTileToCar } from './carTileUtils';
import { VehicleDetailsDialog } from './VehicleDetailsDialog';
import './CarTileWrapper.scss';
import Box from '@mui/material/Box';
import ListItemIcon from '@mui/material/ListItemIcon';
import MenuItem from '@mui/material/MenuItem';

/* eslint-disable @typescript-eslint/no-explicit-any */
// Allowing anys because this file was already littered with them and the time to mess with global components isn't now.

interface ICarTileWrapperProps {
    vehicle: IVehicleTile;
    updatedViewedTiles?: (VehicleTile: IVehicleTile) => any;
    enableRemove: boolean;
    sectionName: string;
    allStockNumbers: number[];
    carouselId: string;
    recommenderCohortId?: string;
    recommenderTestId?: string;
    taggingPosition?: string;
}

export const CarTileWrapper = ({
    vehicle,
    updatedViewedTiles,
    enableRemove,
    sectionName,
    carouselId,
    allStockNumbers,
    recommenderCohortId,
    recommenderTestId,
    taggingPosition,
}: ICarTileWrapperProps) => {
    const stockNumber = vehicle.stockNumber;
    const isUftEnabled = useAppState((state) => state.serveUFTContent);
    const optimizedReturnExperience = useAppState((state) => state.optimizedReturnExperience);
    const decision = useAppState((state) => state.fbsData.find((item) => item.stockNumber === stockNumber));
    const isFavorite = useAppState((state) => state.favoriteVehicles.includes(stockNumber));
    const ref = useRef(null);
    const showRecentlyViewedBadge = optimizedReturnExperience === 'test';
    const car = useMemo(
        () => mapVehicleTileToCar(vehicle, showRecentlyViewedBadge),
        [vehicle, showRecentlyViewedBadge],
    );
    const [isFavoriteInUI, setIsFavoriteInUI] = useState<boolean>(false);
    const [snackbar, setSnackBar] = useState<{ icon: any; text: string; isOpen: boolean }>({
        icon: undefined,
        text: '',
        isOpen: false,
    });
    const [isDialogVisible, setIsDialogVisible] = useState(false);

    const mapDecisionToFbsApplication = (decision: IFBSDecision) => {
        const {
            applicationUrl,
            fbsDecision: { downPayment, expirationDateFormatted, stateAbbreviation, isSoftPull, tradeInBreakdown },
        } = decision;
        return {
            downPayment,
            stateAbbreviation,
            stationUrl: applicationUrl,
            expirationDateFormatted,
            isSoftPull,
            tradeInBreakdown,
        };
    };

    const addVehicleToFavorites = useAppState((state) => state.addVehicleToFavorites);
    const removeVehicleFromFavorites = useAppState((state) => state.removeVehicleFromFavorites);

    const fbsApplication = useMemo(() => {
        if (!decision) {
            return undefined;
        }

        return mapDecisionToFbsApplication(decision);
    }, [decision]);

    const getMenuItems = (vehicle: IVehicleTile) => {
        const menuItems = [
            {
                text: `${isFavoriteInUI ? 'Remove from' : 'Add to'} favorites`,
                icon: Icons.favoriteHeart,
                onClick: () => {
                    handleHeartClick();
                },
                className: `tile-menu-item--favorite ${isFavoriteInUI ? 'saved' : ''}`,
            },
            {
                text: 'Share',
                icon: Icons.share,
                onClick: () => {
                    handleShareClick();
                    const payload: ITrackingPayload = {
                        event: ANALYTICS_CONSTANTS.EVENTS.CLICK.CTA,
                        linkDetails: {
                            name: 'share',
                            position: vehicle.stockNumber.toString(),
                        },
                    };
                    analytics.trackEDDL(payload);
                },
                className: 'tile-menu-item--share',
            },
        ];

        if (vehicle.state) {
            menuItems.push({
                text: 'Features & specs',
                icon: Icons.AdvancedFeaturesIcon,
                onClick: () => {
                    const payload: ITrackingPayload = {
                        event: ANALYTICS_CONSTANTS.EVENTS.CLICK.CTA,
                        linkDetails: {
                            name: 'Features & specs',
                            position: vehicle.stockNumber.toString(),
                        },
                    };
                    analytics.trackEDDL(payload);

                    setIsDialogVisible(true);
                },
                className: 'tile-menu-item--features',
            });
        }

        return menuItems;
    };

    const CRITICAL_MENU_ITEM = {
        text: 'Remove',
        icon: Icons.remove,
        onClick: () => {
            handleRemoveVehicleTile();
            const payload: ITrackingPayload = {
                event: ANALYTICS_CONSTANTS.EVENTS.CLICK.CTA,
                linkDetails: {
                    name: 'remove',
                    position: vehicle.stockNumber.toString(),
                },
            };
            analytics.trackEDDL(payload);
        },
        className: 'tile-menu-item--remove',
    };

    useEffect(() => {
        setIsFavoriteInUI(isFavorite);
    }, [isFavorite]);

    const handleHeartClick = () => {
        setIsFavoriteInUI(!isFavoriteInUI);

        if (isFavoriteInUI) {
            removeVehicleFromFavorites(vehicle.stockNumber);
        } else {
            addVehicleToFavorites(vehicle.stockNumber);
        }
    };

    const handleRemoveVehicleTile = async () => {
        if (updatedViewedTiles) {
            updatedViewedTiles(vehicle);
        }
        removeVehicleFromFavorites(vehicle.stockNumber, true);
    };

    const handleShareClick = async () => {
        if (navigator.share) {
            navigator
                .share({
                    title: `${document.title}.`,
                    text: `Check out ${document.title}.`,
                    url: `${window.location.origin}/car/${vehicle.stockNumber}`,
                })
                .then(() => console.log('Successful Car Tile Share'))
                .catch((error) => console.log('Error Sharing Car Tile', error));
        } else {
            await navigator.clipboard.writeText(`${window.location.origin}/car/${vehicle.stockNumber}`);
            setSnackBar({ icon: Icons.share, isOpen: true, text: 'Link copied to your clipboard' });
        }
    };

    // The TaggingData type isn't actually exposed
    const handleTagging = (taggingData: any) => {
        const linkName = taggingData?.data?.text;
        const isRecommendedCarTile = ANALYTICS_CONSTANTS.RECOMMENDED_SECTION_IDS.includes(carouselId);
        const event = isRecommendedCarTile
            ? ANALYTICS_CONSTANTS.EVENTS.CLICK.RECOMMENDED_CARTILE
            : ANALYTICS_CONSTANTS.EVENTS.CLICK.CTA;
        const payload: ITrackingPayload = {
            event,
            linkDetails: {
                name: linkName,
                position: taggingPosition || sectionName,
            },
        };

        if (isRecommendedCarTile) {
            payload.recommendations = {};
            const allStockNumbersJoined = allStockNumbers.join('|');
            switch (carouselId) {
                case 'local-cars-carousel':
                    // "browse" signifies an unrelated list of vehicles
                    payload.recommendations.browse = allStockNumbersJoined;
                    break;
                case 'kmx-recommended-carousel':
                    // "similarVehicles" signifies a related list of vehicles
                    payload.recommendations.similarVehicles = allStockNumbersJoined;
                    break;
                case 'you-might-like':
                    payload.recommendations.similarVehicles = allStockNumbersJoined;
                    break;
                case 'car-recommendations-carousel':
                    payload.linkDetails = { name: linkName, position: `${taggingPosition}` };
                    payload.recommendations.similarVehicles = allStockNumbersJoined;
                    break;
            }

            payload.recommendations.recommendedCarClicked = `${car.stockNumber}`;
            payload.recommendations.stockNumber = `${car.stockNumber}`;
            payload.module = {
                moduleExperience: analytics.buildModuleExperience(
                    `VR|Homepage|${taggingPosition}`,
                    recommenderCohortId,
                    recommenderTestId,
                ),
            };
        }

        switch (taggingData.event) {
            case TaggingEvent.ClickFavoriteIconButton:
                payload.event = taggingData?.data?.isFavorite
                    ? ANALYTICS_CONSTANTS.EVENTS.CLICK.SAVE_RECOMMENDED_CAR
                    : ANALYTICS_CONSTANTS.EVENTS.CLICK.CTA;
                payload.linkDetails = {
                    name: linkName,
                    position: String(car.stockNumber),
                };
                break;
            default:
                break;
        }

        // Heart clicks are already being tracked so we don't need to track them here
        if (payload.event === ANALYTICS_CONSTANTS.EVENTS.CLICK.SAVE_RECOMMENDED_CAR) {
            return;
        }
        if (payload.linkDetails?.name === 'remove from favorites') {
            return;
        }
        trackEDDL(payload);
    };

    useEffect(() => {
        const onCarTileClick = (e: Event) => {
            const closestAElement = (e.target as HTMLElement)?.closest('a')?.classList;
            const isCarTileAnchor =
                closestAElement?.contains('scct--make-model-info-link') ||
                closestAElement?.contains('scct--image-gallery__image-link');

            if (isCarTileAnchor) {
                e.preventDefault();

                const isRecommendedCarTile = ANALYTICS_CONSTANTS.RECOMMENDED_SECTION_IDS.includes(carouselId);
                const event = isRecommendedCarTile
                    ? ANALYTICS_CONSTANTS.EVENTS.CLICK.RECOMMENDED_CARTILE
                    : ANALYTICS_CONSTANTS.EVENTS.CLICK.CTA;

                const payload: ITrackingPayload = {
                    event,
                    linkDetails: {
                        name: [car.year, car.make, car.model, car.trim].filter((item) => item).join(' '),
                        position: sectionName,
                    },
                };

                if (isRecommendedCarTile) {
                    payload.recommendations = {};
                    const allStockNumbersJoined = allStockNumbers.join('|');
                    switch (carouselId) {
                        case 'local-cars-carousel':
                            // "browse" signifies an unrelated list of vehicles
                            payload.recommendations.browse = allStockNumbersJoined;
                            break;
                        case 'kmx-recommended-carousel':
                            // "similarVehicles" signifies a related list of vehicles
                            payload.recommendations.similarVehicles = allStockNumbersJoined;
                            break;
                        case 'you-might-like':
                            payload.recommendations.similarVehicles = allStockNumbersJoined;
                            break;
                    }

                    payload.recommendations.recommendedCarClicked = `${car.stockNumber}`;
                    payload.recommendations.stockNumber = `${car.stockNumber}`;
                    payload.module = {
                        moduleExperience: analytics.buildModuleExperience(
                            `VR|Homepage|${sectionName}`,
                            recommenderCohortId,
                            recommenderTestId,
                        ),
                    };
                }
                trackEDDL(payload);

                setTimeout(() => {
                    window.location.href = `/car/${car.stockNumber}`;
                }, ANALYTICS_CONSTANTS.TRACKING_DELAY);
            }
        };

        // Add event listener to CarTile
        const tile = ref.current as HTMLElement | null;

        if (tile) {
            tile.addEventListener('click', onCarTileClick);
            return () => {
                tile.removeEventListener('click', onCarTileClick);
            };
        }
    }, [
        allStockNumbers,
        carouselId,
        recommenderCohortId,
        recommenderTestId,
        sectionName,
        car.stockNumber,
        car.year,
        car.make,
        car.model,
        car.trim,
    ]);

    const availabilityInfo = getTileAvailabilityText(vehicle);

    if (isUftEnabled) {
        return (
            <Box>
                <CarTileFuture
                    car={car}
                    imageSrc={vehicle.imageLink}
                    size="medium"
                    availability={availabilityInfo.transferText ?? undefined}
                    favorites={{
                        isFavorited: isFavoriteInUI,
                        onChange: handleHeartClick,
                    }}
                    handleTagging={handleTagging}
                />
            </Box>
        );
    }
    return (
        <div ref={ref}>
            <Box className="car-tile-wrapper__legacy">
                <CarTile
                    car={car}
                    imageSrc={vehicle.imageLink}
                    favorites={{
                        isFavorited: isFavoriteInUI,
                        onChange: handleHeartClick,
                    }}
                    financeInformation={{
                        fbsApplication,
                        fbsDecision: decision?.fbsDecision,
                        downPayment: decision?.fbsDecision.downPayment,
                    }}
                    availability={availabilityInfo.transferText ?? undefined}
                    handleTagging={handleTagging}
                    menuOptions={[
                        ...getMenuItems(vehicle).map((item) => (
                            <MenuItem key={item.className} onClick={item.onClick} className={item.className}>
                                <ListItemIcon
                                    className="car-tile-wrapper-menu-icon"
                                    sx={{
                                        minWidth: (theme) => theme.spacing(4),
                                        '& svg': {
                                            width: (theme) => theme.spacing(2.5),
                                            height: (theme) => theme.spacing(2.5),
                                        },
                                    }}
                                >
                                    {item.icon}
                                </ListItemIcon>
                                {item.text}
                            </MenuItem>
                        )),
                        ...(enableRemove
                            ? [
                                  <MenuItem
                                      key={CRITICAL_MENU_ITEM.className}
                                      onClick={CRITICAL_MENU_ITEM.onClick}
                                      className={CRITICAL_MENU_ITEM.className}
                                  >
                                      <ListItemIcon
                                          className="car-tile-wrapper-menu-icon"
                                          sx={{
                                              minWidth: (theme) => theme.spacing(4),
                                              '& svg': {
                                                  width: (theme) => theme.spacing(2.5),
                                                  height: (theme) => theme.spacing(2.5),
                                              },
                                          }}
                                      >
                                          {CRITICAL_MENU_ITEM.icon}
                                      </ListItemIcon>
                                      {CRITICAL_MENU_ITEM.text}
                                  </MenuItem>,
                              ]
                            : []),
                    ]}
                />
            </Box>

            <VehicleDetailsDialog
                isVisible={isDialogVisible}
                vehicle={vehicle}
                setIsDialogVisible={setIsDialogVisible}
            />

            <TileKebabMenuItemSnackbar
                icon={snackbar.icon}
                isOpen={snackbar.isOpen}
                textLabel={snackbar.text}
                cancelAction={() => setSnackBar({ icon: undefined, isOpen: false, text: '' })}
            />
        </div>
    );
};
